import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaTimes, FaFilePdf, FaCheck, FaPaperPlane } from "react-icons/fa";
import { IoIosRefresh } from "react-icons/io";

interface BillModalProps {
  selectedBill: any;
  changed: boolean;
  showPaymentForm: boolean;
  paymentMethod: string;
  transactionId: string;
  checkboxState: Record<string, boolean>;

  handleClosePaymentForm: () => void;
  handleCheckboxChange: (key: string) => void;
  handleReGenerate: () => void;
  handleViewPdf: () => void;
  setSendInvoiceOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleMarkAsPaid: () => void;
  setShowPaymentForm: React.Dispatch<React.SetStateAction<boolean>>;
  setPaymentMethod: React.Dispatch<React.SetStateAction<string>>;
  setTransactionId: React.Dispatch<React.SetStateAction<string>>;
}

const BillModal: React.FC<BillModalProps> = ({
  selectedBill,
  changed,
  showPaymentForm,
  paymentMethod,
  transactionId,
  checkboxState,
  handleClosePaymentForm,
  handleCheckboxChange,
  handleReGenerate,
  handleViewPdf,
  setSendInvoiceOpen,
  handleMarkAsPaid,
  setShowPaymentForm,
  setPaymentMethod,
  setTransactionId,
}) => {

  if (!selectedBill || selectedBill.orders.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="bill-modal"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50"
      >
        <motion.div
          key="bill-content"
          initial={{ opacity: 0, scale: 0.96, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 24 }}
          transition={{ duration: 0.22, ease: "easeInOut" }}
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden
          inline-block max-w-[90vw] min-w-[320px] max-h-[92vh] w-auto
          flex flex-col relative"
        >
          {/* Header */}
          <div className="sticky top-0 bg-white/90 dark:bg-gray-900/90 p-4 sm:p-5 border-b border-gray-200 dark:border-gray-700 z-10 backdrop-blur rounded-lg">
            <div className="flex items-center justify-between w-full">
              <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight">
                Bill for{" "}
                <span className="text-orange-600 dark:text-orange-400">
                  {selectedBill.label}
                </span>
              </h3>

              <button
                onClick={handleClosePaymentForm}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              >
                <FaTimes size={18} className="sm:w-5 sm:h-5" />
              </button>
            </div>

            {selectedBill?.messageNote?.trim() && (
              <div className="mt-2 text-red-600 dark:text-red-400 font-bold text-sm sm:text-base">
                ⚠ {selectedBill.messageNote}
              </div>
            )}
          </div>

          {/* Body */}
          <div className="p-4 sm:p-6 space-y-6 overflow-y-auto styled-scroll">
            {selectedBill.orders.map((ord: any) => (
              <motion.div
                key={ord.orderId}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.22 }}
                className="bg-gray-50 dark:bg-gray-800/80 p-3 sm:p-4 rounded-xl border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow"
              >
                {/* Order meta */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">
                  <span className="flex items-center gap-1 font-semibold text-gray-800 dark:text-gray-200">
                    {ord.isShowWarning && (
                      <span
                        className="text-red-600 dark:text-red-400"
                        title="Warning ⚠"
                      >
                        ⚠
                      </span>
                    )}
                    Order #{ord.orderId}
                  </span>
                  <span className="mt-1 sm:mt-0 text-gray-500 dark:text-gray-400">
                    {new Date(ord.orderTime).toLocaleString()}
                  </span>
                </div>

                {/* Table header */}
                <div className="grid grid-cols-[1fr_minmax(80px,auto)_minmax(80px,auto)] text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700 pb-1 mb-2">
                  <div>Item</div>
                  <div className="text-right">Discounted/Rate</div>
                  <div className="text-right">Amount</div>
                </div>

                {/* Rows */}
                <div className="space-y-2">
                  {ord.items.map((it: any, idx: number) => {
                    const unit = (it.discountedPrice ?? it.price) as number;
                    const amount = unit * it.qty;
                    return (
                      <div
                        key={idx}
                        className="grid grid-cols-[1fr_minmax(80px,auto)_minmax(80px,auto)] text-sm text-gray-700 dark:text-gray-200 py-1 items-start gap-2"
                      >
                        <div className="whitespace-normal break-words pr-2 min-w-0">
                          {it.name}{" "}
                          <span className="text-gray-500">x {it.qty}</span>
                        </div>
                        <div className="flex justify-end gap-2 whitespace-nowrap">
                          {it.discountedPrice ? (
                            <>
                              <span className="line-through text-gray-400">
                                ₹{it.price.toFixed(2)}
                              </span>
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                ₹{it.discountedPrice.toFixed(2)}
                              </span>
                            </>
                          ) : (
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              ₹{it.price.toFixed(2)}
                            </span>
                          )}
                        </div>
                        <div className="text-right font-medium tabular-nums whitespace-nowrap">
                          ₹{amount.toFixed(2)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white/95 dark:bg-gray-900/95 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 backdrop-blur">
            {/* Summary */}
            <div className="space-y-2 mb-5">
              {selectedBill.billSummary
                .sort((a: any, b: any) => a.sequenceId - b.sequenceId)
                .map((s: any, idx: number) => (
                  <div
                    key={idx}
                    className="flex justify-between items-center text-xs sm:text-sm"
                  >
                    <span className="flex items-center">
                      {s.isCheckBox && (
                        <label className="flex items-center cursor-pointer mr-2">
                          <input
                            type="checkbox"
                            className="form-checkbox h-4 w-4 text-orange-600 dark:text-orange-400 rounded transition-colors"
                            checked={checkboxState[s.key] || false}
                            onChange={() => handleCheckboxChange(s.key)}
                          />
                        </label>
                      )}
                      <span
                        className={`${s.isKeyBold ? "font-semibold" : ""} ${s.keyColourCode ? "" : "text-gray-700 dark:text-gray-300"
                          }`}
                        style={{ color: s.keyColourCode || undefined }}
                      >
                        {s.key}
                        {s.percentText && (
                          <small className="ml-1 text-xs opacity-70">
                            ({s.percentText})
                          </small>
                        )}
                      </span>
                    </span>
                    <span
                      className={`${s.isValueBold
                        ? "font-bold text-sm sm:text-base"
                        : "text-xs sm:text-sm"
                        } ${s.valueColourCode ? "" : "text-gray-900 dark:text-gray-100"}`}
                      style={{ color: s.valueColourCode || undefined }}
                    >
                      {s.value}
                    </span>
                  </div>
                ))}
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-3 gap-2 mb-2">
              <motion.button
                whileTap={{ scale: 0.97 }}
                className={`w-full flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md font-medium text-[12px] transition-colors shadow-sm
                  ${changed ? "bg-orange-600 text-white hover:bg-orange-700" : "bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400 cursor-not-allowed"}`}
                onClick={handleReGenerate}
                disabled={!changed}
              >
                <IoIosRefresh size={13} /> Re-Generate
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.97 }}
                className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md font-medium text-[12px] bg-gray-600 text-white hover:bg-gray-700 transition-colors shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleViewPdf}
                disabled={changed}
              >
                <FaFilePdf size={13} /> View PDF
              </motion.button>

              <motion.button
                whileTap={{ scale: 0.97 }}
                className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md font-medium text-[12px] bg-purple-600 text-white hover:bg-purple-700 transition-colors shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => setSendInvoiceOpen(true)}
              >
                <FaPaperPlane size={13} /> Send Invoice
              </motion.button>

            </div>

            {/* Mark as Paid & Payment Form */}
            {selectedBill.paymentStatus !== "PAID" && (
              <div className="mt-2 w-full">
                {!showPaymentForm ? (
                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    className="w-full flex items-center justify-center gap-1.5 py-1.5 px-3 rounded-md font-medium bg-green-600 text-white hover:bg-green-700 transition-colors shadow-sm text-[12px] disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={() => setShowPaymentForm(true)}
                    disabled={changed}
                  >
                    <FaCheck size={13} /> Mark as Paid
                  </motion.button>
                ) : (
                  <div className="w-full mt-2 space-y-2 border-t border-gray-200 dark:border-gray-700 pt-2">
                    {/* Payment Method */}
                    <div className="space-y-1">
                      <label className="block text-[12px] font-medium text-gray-700 dark:text-gray-300">
                        Payment Method*
                      </label>
                      <div className="relative">
                        <select
                          value={paymentMethod}
                          onChange={(e) => setPaymentMethod(e.target.value)}
                          className="block w-full px-2 py-1.5 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-[12px] text-gray-900 dark:text-gray-100 appearance-none cursor-pointer focus:ring-2 focus:ring-orange-500 transition-colors"
                        >
                          <option value="">Select</option>
                          <option value="CASH">Cash</option>
                          <option value="UPI">UPI</option>
                          <option value="CARD">Card</option>
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-gray-500 dark:text-gray-400">
                          <svg className="fill-current h-3 w-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                            <path d="M9.293 12.95l-.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {/* Transaction ID */}
                    <div className="space-y-1">
                      <label className="block text-[12px] font-medium text-gray-700 dark:text-gray-300">
                        Transaction ID
                      </label>
                      <input
                        type="text"
                        value={transactionId}
                        onChange={(e) => setTransactionId(e.target.value)}
                        placeholder="Optional"
                        className="w-full px-2 py-1.5 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-[12px] text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors"
                      />
                    </div>

                    {/* Confirm / Cancel */}
                    <div className="flex gap-1">
                      <motion.button
                        whileTap={{ scale: 0.97 }}
                        className="flex-1 bg-green-600 text-white py-1.5 px-3 rounded-md font-medium shadow hover:bg-green-700 transition-colors text-[12px]"
                        onClick={handleMarkAsPaid}
                        disabled={changed}
                      >
                        Confirm
                      </motion.button>
                      <motion.button
                        whileTap={{ scale: 0.97 }}
                        className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 py-1.5 px-3 rounded-md font-medium shadow hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-[12px]"
                        onClick={() => setShowPaymentForm(false)}
                      >
                        Cancel
                      </motion.button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Paid Status */}
            {selectedBill.paymentStatus === "PAID" && (
              <div className="w-full flex justify-end">
                <span className="text-sm font-bold text-green-600 flex items-center gap-1.5">
                  <FaCheck size={13} /> Paid
                </span>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>

  );
};

export default BillModal;
